import { Fragment, useState, useEffect, useRef } from 'react'
import * as Yup from 'yup'
import './login.css'
import { useFormik } from 'formik'
import { useAuth } from '../core/Auth'
import { useIsMobile } from '../../../../utils/hooks/useIsMobile'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getUserByToken, login } from '../core/_requests'
import { AuthModel } from '../core/_models'
import * as AuthService from '../../../../services/auth'
import ReCAPTCHA from 'react-google-recaptcha'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  g_recaptcha_response: Yup.string()
    .required('Captcha is required'),
})

const initialValues = {
  email: '',
  password: '',
  g_recaptcha_response: '',
}

export function Login() {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)

  const [loading, setLoading] = useState(false)
  const { saveAuth, setCurrentUser } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [isAlumni, setIsAlumni] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [inputPassword, setInputPassword] = useState({
    type: 'password',
    icon: 'far fa-eye icon',
  })
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)

      try {
        const { email, password, g_recaptcha_response } = values
        const { data } = await AuthService.login({ email, password, g_recaptcha_response })

        const role = data?.role_name

        // Validasi akun salah role
        const isInvalidAlumni = isAlumni && role === 'Company'
        const isInvalidCompany = !isAlumni && role === 'Alumni'

        if (isInvalidAlumni || isInvalidCompany) {
          toast.error('Email atau Password tidak sesuai.')
          return
        }

        // Simpan token dan user
        localStorage.setItem('apiToken', data.token)

        // Transform data to match AuthModel structure
        const authData: AuthModel = {
          api_token: data.token,
          refreshToken: data.refreshToken,
        }
        saveAuth(authData)
        setCurrentUser(data)
        toast('Autentikasi Berhasil Dilakukan')

        // Simpan role
        if (isAlumni) {
          localStorage.setItem('role', 'Alumni')
        } else if (role === 'Company') {
          localStorage.removeItem('alumniPage')
          localStorage.setItem('role', 'company')
        }

        // Role admin & section head
        const isAdmin = ['Super_Admin', 'Admin', 'Section_Head', 'BPK'].includes(role)

        if (isAdmin) {
          localStorage.removeItem('alumniPage')
          localStorage.setItem('role', 'Super_Admin')
          if (role === 'Section_Head') {
            localStorage.setItem('keyRole', 'Section_Head')
          }

          if (role == 'BPK') {
            localStorage.setItem('keyRole', 'BPK')
          }
        }

        const isReviewer = [
          'Reviewer_Substansi_Pana',
          'Reviewer_Administrasi_Pana',
          'Reviewer_Proposal_Pana',
        ].includes(role)
        if (isReviewer) {
          localStorage.removeItem('alumniPage')
          localStorage.setItem('role', role)
        }

        if (role === 'Mitra_Program_PANA') {
          localStorage.removeItem('alumniPage')
          localStorage.setItem('role', 'Mitra_Program_PANA')
          // setTimeout(() => {
          navigate('/laporan-kegiatan-admin', { replace: true })
          // }, 100)
          return
        }

        // Navigate to dashboard instead of reloading
        // Wait for next tick to ensure React has processed state updates
        // The AppRoutes component checks localStorage directly, so it should work
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 100)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
      } finally {
        recaptchaRef.current?.reset()
        formik.setFieldValue('g_recaptcha_response', '') // hilangkan token lama
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    const uri = window.location.href.split('?')
    if (uri.length >= 1) {
      if (uri[1]) {
        setIsAdmin(true)
      }
    }
    if (isAlumni) {
      localStorage.removeItem('role')
    } else {
      localStorage.setItem('role', 'company')
    }
  }, [isAlumni])

  const pageLogin = (type: string) => {
    if (type == 'alumni') {
      setIsAlumni(true)
      return
    }

    setIsAlumni(false)
  }

  const handleChangeEyeIcon = (icon: string) => {
    if (icon == 'far fa-eye icon') {
      setInputPassword({
        type: 'text',
        icon: 'far fa-eye icon fa-eye-slash',
      })
      return
    }

    setInputPassword({
      type: 'password',
      icon: 'far fa-eye icon',
    })
  }

  const handleChangeCaptcha = (value: string | null) => {
    setRecaptchaValue(value)
    formik.setFieldValue('g_recaptcha_response', value || '')
  }

  return (
    <Fragment>
      <div className='d-flex flex-column flex-sm-column flex-md-row'>
        <div
          className='side-one bg-image'
          style={{
            backgroundImage: `url(${isAlumni
              ? require('../../../../assets/img/bg-alumni.png')
              : require('../../../../assets/img/bg-company.png')
              })`,
          }}
        >
          <div className='image-small'>
            <img
              src={
                isAlumni
                  ? require('../../../../assets/img/bg-alumni.png')
                  : require('../../../../assets/img/bg-company.png')
              }
            />
          </div>
        </div>
        <div className='side-two'>
          <div className='form-container'>
            <img
              src={require('../../../../assets/img/login-logo.png')}
              alt=''
              className='login-logo'
            />
            <p className='fontsize-42 fontweight-600 mt-5 mb-8'>
              Selamat datang <br />
              di <span className='color-orange fontweight-600'>Talenta</span>
            </p>

            <div className='text-center m-2'>
              <button
                type='button'
                style={{
                  backgroundColor: isAlumni ? '#FCCDBF' : '',
                  color: isAlumni ? '#F95E2F' : '#667085',
                }}
                className='btn'
                onClick={() => {
                  pageLogin('alumni')
                }}
              >
                Alumni
              </button>
              <button
                type='button'
                style={{
                  backgroundColor: !isAlumni ? '#FCCDBF' : '',
                  color: !isAlumni ? '#F95E2F' : '',
                }}
                className='btn'
                onClick={() => pageLogin('perusahaan')}
              >
                Perusahaan
              </button>
            </div>

            <form className='input-container' onSubmit={formik.handleSubmit}>
              <label className='input-label'>Email</label>
              <input type='email' {...formik.getFieldProps('email')} className='input-form' />

              <label className='input-label'>Password</label>
              <div className='input-icons'>
                <i
                  className={inputPassword.icon}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleChangeEyeIcon(inputPassword.icon)}
                />
                <input
                  type={inputPassword.type}
                  {...formik.getFieldProps('password')}
                  className='input-form'
                />
              </div>

              <div className='space-between mb-30'>
                <p>
                  {isAlumni ? (
                    <a
                      href='https://beasiswalpdp.kemenkeu.go.id/index.php/site/resetPassword'
                      className='color-orange fontweight-600'
                      target='_blank'
                      rel='noreferrer'
                    >
                      Lupa Password?
                    </a>
                  ) : (
                    <Link to={'/forgot-password'} className='color-orange fontweight-600'>
                      Lupa Password?
                    </Link>
                  )}
                </p>
              </div>

              {/* ReCAPTCHA */}
              <div className='mb-10'>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey='6Lf3qh8qAAAAAAmCYQOBXog8koATjbM4w7HZBGo2'
                  onChange={(value) => formik.setFieldValue('g_recaptcha_response', value)}
                  onExpired={() => {
                    formik.setFieldValue('g_recaptcha_response', '')
                    toast.error('Captcha expired, silakan centang ulang.')
                  }}
                />

                {formik.touched.g_recaptcha_response && formik.errors.g_recaptcha_response && (
                  <div className='text-danger small mt-1'>{formik.errors.g_recaptcha_response}</div>
                )}
              </div>

              <button className='button mb-5' disabled={formik.isSubmitting || !formik.isValid}>
                {loading ? 'Loading ...' : 'Masuk'}
              </button>

              <p className='text-center fontsize-14 mb-18'>
                <p>
                  {isAlumni ? (
                    <>
                      <span className='color-grey'>Belum Punya Aktivasi?</span>{' '}
                      <Link to={'/activation'} className='color-dark-blue'>
                        Ajukan permintaan aktivasi
                      </Link>
                    </>
                  ) : (
                    <>
                      <span className='color-grey'>Belum Punya Akun?</span>{' '}
                      <Link to={'/company-register'} className='color-dark-blue'>
                        Buat Akun di sini
                      </Link>
                    </>
                  )}
                </p>
              </p>

              <p className='text-center fontsize-14 color-grey' style={{ maxWidth: '500px' }}>
                Tidak ada bagian dari situs web ini atau isinya yang boleh direproduksi, disalin,
                dimodifikasi atau diadaptasi, tanpa persetujuan tertulis sebelumnya dari LPDP.
              </p>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  )
}
